import React from 'react';
import { Command } from 'cmdk';
import styles from '../CommandPalette.module.scss';

export interface CommandsSkeletonProps {
  /**
   * Number of skeleton rows to render.
   * @default 5
   */
  count?: number;
}

/**
 * CommandsSkeleton - Drop-in loading placeholder for a command list.
 *
 * Renders inside a `Command.List` and mimics the visual structure of command
 * items with an animated shimmer. Use it in plugin page components while
 * async data is being fetched.
 *
 * @example
 * ```tsx
 * const MyPage: React.FC = () => {
 *   const { data, isLoading } = useMyData();
 *   if (isLoading) return <CommandsSkeleton count={4} />;
 *   return <CommandList commands={data} />;
 * };
 * ```
 */
export const CommandsSkeleton: React.FC<CommandsSkeletonProps> = ({ count = 5 }) => {
  return (
    <Command.List>
      {Array.from({ length: count }, (_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className={styles.skeletonItem} aria-hidden="true">
          <div className={styles.skeletonIcon} />
          <div className={styles.skeletonContent}>
            <div className={styles.skeletonLabel} />
            <div className={styles.skeletonDescription} />
          </div>
        </div>
      ))}
    </Command.List>
  );
};
